import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res.ok) navigate("/", { replace: true });
      else setError(res.error);
    } catch {
      setError("Die Anmeldung konnte nicht abgeschlossen werden. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: form (light, editorial) */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm animate-rise">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 rounded-xl bg-neutral-900 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-head font-bold text-neutral-900 leading-tight">Redaktionsleitstand</div>
              <div className="text-xs text-neutral-400">Content Operating System</div>
            </div>
          </div>

          <h1 className="font-head text-3xl font-bold text-neutral-900 tracking-tight">Willkommen zurück</h1>
          <p className="text-neutral-500 mt-2 text-sm">Melde dich an, um deine Inhalte zu steuern.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium text-neutral-700">E-Mail</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                placeholder="name@bruchkoebel.de"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700">Passwort</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-neutral-900 text-white font-medium hover:bg-neutral-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Anmelden
            </button>
          </form>

          <div className="mt-8 flex items-center gap-2.5" data-testid="login-innovationshafen-branding">
            <span className="text-[11px] text-neutral-400">Eine Initiative von</span>
            <a href="https://innovationshafen.net" target="_blank" rel="noopener noreferrer" aria-label="Innovationshafen">
              <img
                src="/innovationshafen-logo.png"
                alt="Innovationshafen"
                className="h-4 w-auto opacity-80 hover:opacity-100 transition-opacity"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Right: premium dark panel */}
      <div className="hidden lg:flex flex-1 relative bg-neutral-900 overflow-hidden items-center justify-center p-12">
        <img
          src="https://images.pexels.com/photos/10464465/pexels-photo-10464465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-neutral-900 via-neutral-900/80 to-transparent" />
        <div className="relative max-w-md text-white">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-blue-400">Lokale Reichweite, professionell gesteuert</p>
          <h2 className="font-head text-4xl font-bold mt-4 leading-tight">
            Vom Thema bis zur Freigabe — alles in einem Leitstand.
          </h2>
          <p className="text-neutral-300 mt-4 leading-relaxed">
            Themenradar, Content-Pipeline, Freigabe-Center und Kalender. Klar, schnell, hochwertig.
          </p>

          <div className="mt-12 pt-6 border-t border-white/10 flex items-center gap-3" data-testid="login-innovationshafen-darkbrand">
            <div className="bg-white rounded-lg px-3 py-1.5 inline-flex">
              <img
                src="/innovationshafen-logo.png"
                alt="Innovationshafen"
                className="h-4 w-auto"
              />
            </div>
            <span className="text-[11px] text-neutral-500">socialmedia.innovationshafen.net</span>
          </div>
        </div>
      </div>
    </div>
  );
}
